import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, User, Bot, ChevronRight, CheckCircle, Building2, ArrowRight, Sparkles, Phone, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { API } from '../utils/api';


// Quick test questions
const TEST_QUESTIONS = [
  {
    id: 'region',
    question: '¿En qué región de Chile te encuentras?',
    options: ['Región Metropolitana', 'Valparaíso', 'Biobío', 'Maule', 'La Araucanía', 'O\'Higgins', 'Los Lagos', 'Coquimbo', 'Antofagasta', 'Otra región']
  },
  {
    id: 'substance',
    question: '¿Qué sustancia o comportamiento te preocupa?',
    options: ['Alcohol', 'Cocaína/Pasta base', 'Marihuana', 'Medicamentos', 'Juego/Apuestas', 'Otro']
  },
  {
    id: 'frequency',
    question: '¿Con qué frecuencia consumes o practicas este comportamiento?',
    options: ['Ocasionalmente (1-2 veces/mes)', 'Semanalmente', 'Varios días a la semana', 'Diariamente']
  },
  {
    id: 'impact',
    question: '¿Cómo ha afectado tu vida?',
    options: ['Poco impacto', 'Problemas con familia/amigos', 'Problemas en el trabajo/estudios', 'Problemas de salud', 'Múltiples áreas afectadas']
  },
  {
    id: 'attempts',
    question: '¿Has intentado dejarlo antes?',
    options: ['Nunca lo he intentado', 'Sí, con algo de éxito', 'Sí, pero recaí', 'Múltiples intentos sin éxito']
  }
];

function TerapeutAI() {
  const location = useLocation();
  // Ocultar en experiencias inmersivas y flujos de dashboard/auth
  const hiddenPrefixes = ['/terapeutia', '/terapeut-ia', '/terapeut-ai', '/experiencia', '/yo-del-futuro', '/espejo', '/dashboard', '/admin', '/sales-dashboard', '/login', '/auth', '/register', '/profile', '/checkout', '/payment', '/webpay', '/professional-dashboard', '/workshop-dashboard', '/center-dashboard', '/center/', '/professional/', '/workshop/', '/ventas', '/tratamiento-adicciones-online', '/encuesta/', '/coach-adicciones'];
  const hidden = hiddenPrefixes.some((p) => location.pathname.startsWith(p));

  const [isOpen, setIsOpen] = useState(false);
  const [stage, setStage] = useState('welcome'); // welcome, options, email, test, chat
  const [email, setEmail] = useState('');
  const [sessionId] = useState(() => 'chat_' + Math.random().toString(36).substr(2, 9));
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [testAnswers, setTestAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [recommendation, setRecommendation] = useState(null);
  const [suggestedCenters, setSuggestedCenters] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null); // track what user wants
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && stage === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, stage]);

  async function handleEmailSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    
    try {
      await axios.post(`${API}/chat/register-lead?email=${encodeURIComponent(email)}&session_id=${sessionId}`);
      // Go to test if user selected "help", otherwise go to chat
      if (selectedOption === 'help') {
        setStage('test');
      } else {
        // Go directly to chat for "other" option
        setMessages([{ role: 'assistant', content: '¡Hola! Estoy aquí para ayudarte con cualquier consulta sobre adicciones, nuestra plataforma, centros de rehabilitación, profesionales o cualquier información que necesites. ¿En qué puedo ayudarte?' }]);
        setStage('chat');
      }
    } catch (err) {
      console.error(err);
      if (selectedOption === 'help') {
        setStage('test');
      } else {
        setMessages([{ role: 'assistant', content: '¡Hola! Estoy aquí para ayudarte con cualquier consulta sobre adicciones, nuestra plataforma, centros de rehabilitación, profesionales o cualquier información que necesites. ¿En qué puedo ayudarte?' }]);
        setStage('chat');
      }
    }
  }

  function handleSkipEmail() {
    setEmail('anonimo@temp.com');
    if (selectedOption === 'help') {
      setStage('test');
    } else {
      setMessages([{ role: 'assistant', content: '¡Hola! Estoy aquí para ayudarte con cualquier consulta sobre adicciones, nuestra plataforma, centros de rehabilitación, profesionales o cualquier información que necesites. ¿En qué puedo ayudarte?' }]);
      setStage('chat');
    }
  }

  function handleTestAnswer(answer) {
    const question = TEST_QUESTIONS[currentQuestion];
    const newAnswers = { ...testAnswers, [question.id]: answer };
    setTestAnswers(newAnswers);

    if (currentQuestion < TEST_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate recommendation
      finishTest(newAnswers);
    }
  }

  async function finishTest(answers) {
    // Determine recommendation based on answers
    let rec = 'ambulatorio';
    const frequency = answers.frequency;
    const impact = answers.impact;
    const attempts = answers.attempts;

    if (
      frequency === 'Diariamente' ||
      impact === 'Múltiples áreas afectadas' ||
      attempts === 'Múltiples intentos sin éxito'
    ) {
      rec = 'residencial';
    } else if (
      frequency === 'Varios días a la semana' ||
      impact === 'Problemas de salud' ||
      attempts === 'Sí, pero recaí'
    ) {
      rec = 'ambulatorio_intensivo';
    } else if (
      frequency === 'Ocasionalmente (1-2 veces/mes)' &&
      impact === 'Poco impacto'
    ) {
      rec = 'online';
    }

    setRecommendation(rec);

    // Save test results
    try {
      await axios.post(`${API}/chat/save-test-results`, {
        session_id: sessionId,
        email: email,
        test_results: answers,
        recommendation: rec
      });

      // Get suggested centers with region filter
      const region = answers.region || '';
      const centersRes = await axios.get(`${API}/chat/suggested-centers?recommendation=${rec}&region=${encodeURIComponent(region)}`);
      setSuggestedCenters(centersRes.data.centers || []);
    } catch (err) {
      console.error(err);
    }

    // Generate initial AI message based on results
    const initialMessage = getRecommendationMessage(rec, answers);
    setMessages([{ role: 'assistant', content: initialMessage }]);
    setStage('chat');
  }

  function getRecommendationMessage(rec, answers) {
    const substance = answers.substance || 'esta situación';
    const region = answers.region || '';
    let msg = `Gracias por compartir esta información conmigo. Basándome en lo que me cuentas sobre ${substance.toLowerCase()}, `;

    if (rec === 'residencial') {
      msg += `te recomendaría considerar un **tratamiento residencial**. Este tipo de programa ofrece un ambiente controlado las 24 horas, ideal cuando hay consumo diario o múltiples áreas de la vida afectadas.\n\n`;
      msg += `Un centro residencial te brindará:\n- Desintoxicación supervisada\n- Terapia individual y grupal intensiva\n- Alejamiento de desencadenantes\n- Estructura y apoyo constante`;
    } else if (rec === 'ambulatorio_intensivo') {
      msg += `te sugeriría un **tratamiento ambulatorio intensivo**. Este programa permite mantener tu rutina mientras recibes ayuda profesional regular.\n\n`;
      msg += `Incluye:\n- Sesiones de terapia varias veces por semana\n- Grupos de apoyo\n- Seguimiento médico si es necesario`;
    } else if (rec === 'ambulatorio') {
      msg += `un **tratamiento ambulatorio** podría ser una buena opción inicial. Te permite trabajar en tu recuperación mientras mantienes tus actividades.\n\n`;
      msg += `Considera:\n- Terapia individual semanal\n- Grupos de apoyo como AA o NA\n- Evaluación periódica del progreso`;
    } else {
      msg += `podrías comenzar con **apoyo online y grupos de autoayuda**. Esto incluye:\n- Terapia online\n- Grupos de apoyo virtuales\n- Recursos de autoayuda`;
    }

    if (region) {
      msg += `\n\nHe filtrado centros en **${region}** para ti.`;
    }

    msg += `\n\n🎯 **¿Necesitas orientación personalizada?**\nTe recomiendo solicitar una [**Asesoría Experta**](/asesoria) donde un profesional te guiará según tu situación específica.`;
    msg += `\n\n¿Tienes alguna pregunta sobre estos tratamientos o sobre adicciones en general?`;

    return msg;
  }

  // Render message with clickable links
  function renderMessage(content) {
    // Convert markdown links [text](/url) to clickable links
    // Also handle raw URLs that start with /
    const parts = content.split(/(\[.*?\]\(.*?\)|\/[a-z0-9-]+(?=\s|\)|$|,|\.|\]))/gi);
    return parts.map((part, idx) => {
      // Check for markdown link format [text](url)
      const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
      if (linkMatch) {
        const linkUrl = linkMatch[2].startsWith('/') ? linkMatch[2] : linkMatch[2];
        return (
          <a 
            key={idx} 
            href={linkUrl} 
            className="text-emerald-400 hover:text-emerald-300 underline font-medium inline-flex items-center gap-1"
            onClick={(e) => {
              if (linkUrl.startsWith('/')) {
                e.preventDefault();
                window.location.href = linkUrl;
              }
            }}
            target={linkUrl.startsWith('http') ? '_blank' : '_self'}
            rel={linkUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
          >
            {linkMatch[1]}
          </a>
        );
      }
      // Check for raw URL paths like /base-de-datos-gratis
      if (part.startsWith('/') && part.length > 1 && !part.includes(' ')) {
        return (
          <a 
            key={idx} 
            href={part} 
            className="text-emerald-400 hover:text-emerald-300 underline font-medium"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = part;
            }}
          >
            {part}
          </a>
        );
      }
      // Handle **bold** text
      const boldParts = part.split(/(\*\*.*?\*\*)/g);
      return boldParts.map((bp, bidx) => {
        if (bp.startsWith('**') && bp.endsWith('**')) {
          return <strong key={`${idx}-${bidx}`} className="font-semibold">{bp.slice(2, -2)}</strong>;
        }
        return bp;
      });
    });
  }

  async function handleSendMessage(e) {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: userMessage,
        email: email
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, hubo un error. Por favor, intenta de nuevo o visita nuestra sección de Asesoría Experta para hablar con un profesional.' 
      }]);
    }
    setIsLoading(false);
  }

  function resetChat() {
    setStage('welcome');
    setEmail('');
    setMessages([]);
    setTestAnswers({});
    setCurrentQuestion(0);
    setRecommendation(null);
    setSuggestedCenters([]);
  }

  if (hidden) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-4 sm:right-24 z-50 w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-slate-700 hover:bg-slate-600 rotate-0' 
            : 'bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 animate-pulse hover:animate-none'
        }`}
        data-testid="terapeut-ai-button"
      >
        {isOpen ? (
          <X className="w-7 h-7 text-white" />
        ) : (
          <MessageCircle className="w-7 h-7 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-4 sm:right-24 z-50 w-[calc(100vw-32px)] sm:w-[380px] max-w-[400px] h-[70vh] sm:h-[560px] max-h-[calc(100vh-120px)] bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 flex flex-col overflow-hidden"
          data-testid="terapeut-ai-chat"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">terapeutIA</h3>
              <p className="text-xs text-emerald-100">Orientación en adicciones</p>
            </div>
            {stage === 'chat' && (
              <button onClick={resetChat} className="text-white/70 hover:text-white text-xs underline">
                Reiniciar
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Welcome Stage */}
            {stage === 'welcome' && (
              <div className="h-full flex flex-col justify-center items-center text-center px-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-6">
                  <Bot className="w-10 h-10 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">¡Hola! Soy terapeutIA</h2>
                <p className="text-slate-400 mb-6 text-sm">
                  Estoy aquí para orientarte. ¿Cómo puedo ayudarte hoy?
                </p>
                <div className="w-full space-y-3">
                  <Button 
                    onClick={() => { setSelectedOption('info'); setStage('options'); }}
                    variant="outline"
                    className="w-full border-slate-700 text-slate-200 hover:bg-slate-800 justify-start"
                    data-testid="option-info-button"
                  >
                    <Building2 className="w-4 h-4 mr-3 text-emerald-400" />
                    Información de la plataforma
                  </Button>
                  <Button 
                    onClick={() => { setSelectedOption('help'); setStage('email'); }}
                    variant="outline"
                    className="w-full border-slate-700 text-slate-200 hover:bg-slate-800 justify-start"
                    data-testid="option-help-button"
                  >
                    <CheckCircle className="w-4 h-4 mr-3 text-blue-400" />
                    Busco ayuda (Test de orientación)
                  </Button>
                  <Button 
                    onClick={() => { setSelectedOption('other'); setStage('email'); }}
                    variant="outline"
                    className="w-full border-slate-700 text-slate-200 hover:bg-slate-800 justify-start"
                    data-testid="option-other-button"
                  >
                    <MessageCircle className="w-4 h-4 mr-3 text-purple-400" />
                    Otra consulta
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-4">
                  Toda la información es confidencial
                </p>
              </div>
            )}

            {/* Options Stage - Info about platform */}
            {stage === 'options' && (
              <div className="h-full flex flex-col px-2 py-4">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-white mb-2">¿Qué te gustaría conocer?</h3>
                </div>
                <div className="space-y-2 flex-1">
                  <button 
                    onClick={() => { 
                      setMessages([{ role: 'assistant', content: '🏥 **Centros de Rehabilitación**\n\nEn SinAdicciones.org encontrarás un directorio completo de centros de rehabilitación en Chile.\n\n**¿Qué puedes encontrar?**\n- Centros residenciales y ambulatorios\n- Filtros por ciudad, precio y tipo de tratamiento\n- Información de contacto y WhatsApp directo\n- Reseñas de otros pacientes\n\n👉 Ver directorio: [/centros](/centros)\n\n**¿Tienes un centro?**\n✨ Regístralo gratis: [/agregar-centro](/agregar-centro)\n\n¿Tienes alguna pregunta específica sobre los centros?' }]);
                      setStage('chat');
                    }}
                    className="w-full p-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-left transition-colors"
                  >
                    <p className="text-white font-medium flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-emerald-400" /> Centros de Rehabilitación
                    </p>
                    <p className="text-slate-400 text-xs mt-1">Directorio de centros en Chile</p>
                  </button>
                  <button 
                    onClick={() => { 
                      setMessages([{ role: 'assistant', content: '👨‍⚕️ **Profesionales de Salud Mental**\n\nConectamos con psicólogos, psiquiatras y terapeutas especializados en adicciones.\n\n**Servicios disponibles:**\n- Consultas presenciales y online\n- Agenda citas directamente desde la plataforma\n- Pago seguro con Mercado Pago\n- Profesionales verificados\n\n**Precios:** Varían según el profesional (desde $15.000 CLP la sesión)\n\n👉 Ver profesionales: [/profesionales](/profesionales)\n\n**¿Eres profesional de salud mental?**\n✨ Únete a nuestra red: [/unirse-como-profesional](/unirse-como-profesional)\n\n¿Te gustaría saber más sobre algún tipo de profesional en particular?' }]);
                      setStage('chat');
                    }}
                    className="w-full p-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-left transition-colors"
                  >
                    <p className="text-white font-medium flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-400" /> Profesionales
                    </p>
                    <p className="text-slate-400 text-xs mt-1">Psicólogos y terapeutas</p>
                  </button>
                  <button 
                    onClick={() => { 
                      setMessages([{ role: 'assistant', content: '🎓 **Talleres de Rehabilitación**\n\nOfrecemos un directorio de talleres especializados en recuperación de adicciones.\n\n**Tipos de talleres:**\n- Grupos de apoyo (AA, NA)\n- Talleres artísticos y ocupacionales\n- Mindfulness y meditación\n- Habilidades para la vida\n\n**Modalidades:** Presencial, online o híbrido\n\n👉 Ver talleres: [/talleres](/talleres)\n\n**¿Ofreces talleres terapéuticos?**\n✨ Registra tu taller: [/registrar-taller](/registrar-taller)\n\n¿Buscas algún tipo de taller en específico?' }]);
                      setStage('chat');
                    }}
                    className="w-full p-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-left transition-colors"
                  >
                    <p className="text-white font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" /> Talleres
                    </p>
                    <p className="text-slate-400 text-xs mt-1">Grupos de apoyo y actividades</p>
                  </button>
                  <button 
                    onClick={() => { 
                      setMessages([{ role: 'assistant', content: '📱 **Sobre SinAdicciones.org**\n\nSomos una plataforma chilena dedicada a conectar personas con recursos para la recuperación de adicciones.\n\n**Nuestros servicios:**\n- 🏥 Directorio de centros de rehabilitación\n- 👨‍⚕️ Profesionales de salud mental\n- 🎓 Talleres y grupos de apoyo\n- 📝 Test de autoevaluación gratuito\n- 📚 Blog con contenido educativo\n- 📱 App móvil (próximamente)\n\n**Precios:**\n- Buscar en el directorio: Gratis\n- Consultas con profesionales: Desde $15.000 CLP\n- Asesoría experta personalizada: Consultar precios\n\n**¿Quieres unirte a nuestra red?**\n- 🏥 [Agregar Centro](/agregar-centro)\n- 👨‍⚕️ [Unirse como Profesional](/unirse-como-profesional)\n- 🎓 [Registrar Taller](/registrar-taller)\n\n¿En qué más puedo ayudarte?' }]);
                      setStage('chat');
                    }}
                    className="w-full p-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-left transition-colors"
                  >
                    <p className="text-white font-medium flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-400" /> Información General
                    </p>
                    <p className="text-slate-400 text-xs mt-1">Precios, servicios y más</p>
                  </button>
                </div>
                <button 
                  onClick={() => setStage('welcome')}
                  className="text-slate-500 text-xs mt-4 hover:text-slate-400 text-center w-full"
                >
                  ← Volver
                </button>
              </div>
            )}

            {/* Email Stage */}
            {stage === 'email' && (
              <div className="h-full flex flex-col justify-center px-2">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {selectedOption === 'help' ? 'Antes de comenzar el test' : 'Para brindarte mejor atención'}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {selectedOption === 'help' 
                      ? 'Déjanos tu email para enviarte información útil y recursos sobre recuperación.'
                      : 'Opcionalmente, déjanos tu email para un seguimiento personalizado.'}
                  </p>
                </div>
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="chat-email-input"
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    data-testid="submit-email-button"
                  >
                    Continuar
                  </Button>
                </form>
                <button 
                  onClick={handleSkipEmail}
                  className="text-slate-500 text-xs mt-4 hover:text-slate-400 text-center w-full"
                >
                  Prefiero no dar mi email
                </button>
                <button 
                  onClick={() => setStage('welcome')}
                  className="text-slate-500 text-xs mt-2 hover:text-slate-400 text-center w-full"
                >
                  ← Volver
                </button>
              </div>
            )}

            {/* Test Stage */}
            {stage === 'test' && (
              <div className="h-full flex flex-col">
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-slate-500">Pregunta {currentQuestion + 1} de {TEST_QUESTIONS.length}</span>
                    <span className="text-xs text-emerald-400">{Math.round(((currentQuestion + 1) / TEST_QUESTIONS.length) * 100)}%</span>
                  </div>
                  <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 transition-all duration-300"
                      style={{ width: `${((currentQuestion + 1) / TEST_QUESTIONS.length) * 100}%` }}
                    />
                  </div>
                </div>
                
                <h3 className="text-white font-medium mb-4">
                  {TEST_QUESTIONS[currentQuestion].question}
                </h3>
                
                <div className="space-y-2">
                  {TEST_QUESTIONS[currentQuestion].options.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleTestAnswer(option)}
                      className="w-full p-3 text-left rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-emerald-500/50 text-slate-300 hover:text-white transition-all text-sm"
                      data-testid={`test-option-${idx}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Stage */}
            {stage === 'chat' && (
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user' 
                        ? 'bg-emerald-500 text-white rounded-br-md' 
                        : 'bg-slate-800 text-slate-200 rounded-bl-md'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{renderMessage(msg.content)}</p>
                      {/* Quick action buttons for bot messages */}
                      {msg.role === 'assistant' && idx === messages.length - 1 && (
                        <div className="mt-3 pt-3 border-t border-slate-700/50 flex flex-wrap gap-2">
                          <a
                            href="https://wa.me/56928341147?text=Hola,%20vengo%20del%20chat%20de%20SinAdicciones%20y%20necesito%20ayuda"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-500/20 text-green-400 rounded-full hover:bg-green-500/30 transition-colors"
                          >
                            <Phone className="w-3 h-3" />
                            Hablar con Ejecutivo
                          </a>
                          <a
                            href="/centros"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full hover:bg-emerald-500/30 transition-colors"
                            onClick={(e) => { e.preventDefault(); window.location.href = '/centros'; }}
                          >
                            <Building2 className="w-3 h-3" />
                            Ver Centros
                          </a>
                          <a
                            href="/asesoria"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-violet-500/20 text-violet-400 rounded-full hover:bg-violet-500/30 transition-colors"
                            onClick={(e) => { e.preventDefault(); window.location.href = '/asesoria'; }}
                          >
                            <Sparkles className="w-3 h-3" />
                            Asesoría Experta
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Suggested Centers */}
                {suggestedCenters.length > 0 && messages.length === 1 && (
                  <div className="mt-4 space-y-4">
                    {/* Asesoría Experta CTA */}
                    <a
                      href="/asesoria"
                      className="flex items-center gap-3 p-3 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl hover:border-emerald-500/50 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">Asesoría Experta</p>
                        <p className="text-xs text-slate-400">Orientación personalizada de un profesional</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                    </a>

                    {/* WhatsApp Contact CTA */}
                    <a
                      href="https://wa.me/56928341147?text=Hola%2C%20necesito%20orientaci%C3%B3n%20sobre%20tratamientos%20de%20adicciones"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 rounded-xl hover:border-green-500/50 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">Contactar al equipo</p>
                        <p className="text-xs text-slate-400">+56 9 2834 1147 • WhatsApp</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-green-400 group-hover:translate-x-1 transition-transform" />
                    </a>

                    <p className="text-xs text-slate-500">Centros recomendados para ti:</p>
                    <div className="space-y-2">
                      {suggestedCenters.map((center) => (
                        <a
                          key={center.center_id}
                          href={`/centros/${center.center_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-2 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors group"
                        >
                          {center.logo ? (
                            <img src={center.logo} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate flex items-center gap-1">
                              {center.name}
                              {center.is_premium && <Crown className="w-3 h-3 text-amber-400" />}
                              {center.is_verified && <CheckCircle className="w-3 h-3 text-emerald-400" />}
                            </p>
                            <p className="text-xs text-slate-500">{center.city}</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area - Only in chat stage */}
          {stage === 'chat' && (
            <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-700 bg-slate-800/50">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 bg-slate-900 border-slate-700 text-white"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  className="bg-emerald-500 hover:bg-emerald-600"
                  disabled={isLoading || !inputValue.trim()}
                  data-testid="send-message-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 mt-2 text-center">
                terapeutIA es orientativo. Consulta siempre con un profesional.
              </p>
            </form>
          )}
        </div>
      )}
    </>
  );
}

export default TerapeutAI;
